import React, { useState, useEffect } from 'react';
import { X, Plus, BookOpen, Users, TrendingUp, DollarSign, Edit, Eye, Trash2 } from 'lucide-react';
import { courseService } from '../../../services/courseService';
import CreateCourse from './CreateCourse';
import { CourseEditor } from './CourseEditor';
import { motion, AnimatePresence } from 'framer-motion';

export const CourseManager = ({ bookId, user, isOpen, onClose, onUpdate }) => {
  const [view, setView] = useState('dashboard'); // dashboard, create, edit
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalStudents: 0,
    totalRevenue: 0,
    averageRating: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadCourses();
    }
  }, [isOpen, bookId]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const creatorCourses = await courseService.getCreatorCourses(bookId, user.uid);
      setCourses(creatorCourses);

      const totalStudents = creatorCourses.reduce((sum, c) => sum + (c.enrollmentCount || 0), 0);
      const totalRevenue = creatorCourses.reduce((sum, c) => sum + ((c.enrollmentCount || 0) * (c.price || 0)), 0);
      const avgRating = creatorCourses.reduce((sum, c) => sum + (c.rating || 0), 0) / (creatorCourses.length || 1);

      setStats({
        totalCourses: creatorCourses.length,
        totalStudents,
        totalRevenue,
        averageRating: avgRating.toFixed(1)
      });

    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = () => {
    setView('create');
  };

  const handleEditCourse = (course) => {
    setSelectedCourse(course);
    setView('edit');
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;

    try {
      await courseService.deleteCourse(bookId, courseId);
      await loadCourses();
      alert('Course deleted successfully');
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course');
    }
  };

  const handleBackToDashboard = () => {
    setView('dashboard');
    setSelectedCourse(null);
    loadCourses();
  };

  if (!isOpen) return null;

  // Render Create Course View
  if (view === 'create') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <CreateCourse 
            bookId={bookId} 
            user={user}
            onBack={handleBackToDashboard}
            onComplete={handleBackToDashboard}
            embedded={true}
          />
        </div>
      </div>
    );
  }

  // Render Course Editor View
  if (view === 'edit' && selectedCourse) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
          <CourseEditor 
            bookId={bookId} 
            courseId={selectedCourse.id}
            user={user}
            onBack={handleBackToDashboard}
            embedded={true}
          />
        </div>
      </div>
    );
  }

  // Render Dashboard View
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Course Manager</h2>
            <p className="text-gray-600">Manage your courses and track performance</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCreateCourse}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Course
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <BookOpen className="w-6 h-6 text-blue-500" />
                <span className="text-2xl font-bold text-gray-900">{stats.totalCourses}</span>
              </div>
              <div className="text-sm text-gray-600">Total Courses</div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-6 h-6 text-green-500" />
                <span className="text-2xl font-bold text-gray-900">{stats.totalStudents}</span>
              </div>
              <div className="text-sm text-gray-600">Total Students</div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-6 h-6 text-yellow-500" />
                <span className="text-2xl font-bold text-gray-900">${stats.totalRevenue}</span>
              </div>
              <div className="text-sm text-gray-600">Total Revenue</div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-6 h-6 text-purple-500" />
                <span className="text-2xl font-bold text-gray-900">{stats.averageRating}</span>
              </div>
              <div className="text-sm text-gray-600">Avg Rating</div>
            </div>
          </div>
        </div>

        {/* Courses List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No courses yet</h3>
              <p className="text-gray-600 mb-6">Create your first course to start teaching</p>
              <button
                onClick={handleCreateCourse}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
              >
                Create Your First Course
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {courses.map((course) => (
                <div key={course.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900">{course.title}</h3>
                        {course.isPublished ? (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                            Published
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">
                            Draft
                          </span>
                        )}
                        {course.isPremium && (
                          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                            ${course.price}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-4">{course.description}</p>
                      <div className="flex items-center gap-6 text-sm text-gray-500">
                        <span>{course.enrollmentCount || 0} students</span>
                        <span>{course.lessonCount || 0} lessons</span>
                        <span>★ {course.rating?.toFixed(1) || '0.0'}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleEditCourse(course)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                        title="Edit Course"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteCourse(course.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Delete Course"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};